import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Cake, Gift, ChevronRight, X } from 'lucide-react';
import { dashboardAPI } from '@/lib/api';

const DISMISS_KEY = 'kr-birthday-banner-dismissed-date';

/**
 * Prominent above-the-fold banner shown immediately under "Welcome, ..."
 * on the admin/principal dashboard. Only renders when there are birthdays
 * within the next N days. Visually loud (pink gradient + cake icon) so it
 * can't be missed at login. Dismissible per-day per-browser.
 */
export function TopBirthdayBanner({ days = 14, maxAvatars = 5 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    if (typeof window !== 'undefined' && window.localStorage?.getItem(DISMISS_KEY) === today) {
      setDismissed(true);
    }
    let cancelled = false;
    dashboardAPI.upcomingBirthdays(days)
      .then((r) => { if (!cancelled) setItems(r.data.birthdays || []); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  const dismiss = () => {
    const today = new Date().toISOString().slice(0, 10);
    try { window.localStorage.setItem(DISMISS_KEY, today); } catch { /* ignore */ }
    setDismissed(true);
  };

  if (loading || dismissed || items.length === 0) return null;

  const today = items.filter((b) => b.is_today);
  const upcoming = items.filter((b) => !b.is_today);
  const visible = (today.length ? today : upcoming).slice(0, maxAvatars);
  const remainder = items.length - visible.length;

  const heading = today.length > 0
    ? `🎂 ${today.length} birthday${today.length === 1 ? '' : 's'} today!`
    : `${items.length} upcoming birthday${items.length === 1 ? '' : 's'} in the next ${days} days`;

  return (
    <Card
      className="mb-6 border-l-4 border-l-pink-500 bg-gradient-to-r from-pink-50 via-rose-50 to-amber-50 shadow-sm animate-slideIn"
      data-testid="top-birthday-banner"
    >
      <CardContent className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <button
            type="button"
            onClick={() => navigate('/birthdays')}
            className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 text-white flex items-center justify-center shrink-0 shadow-sm hover:scale-105 transition"
            data-testid="birthday-banner-icon"
            title="View all upcoming birthdays"
          >
            {today.length > 0 ? <Gift className="h-6 w-6" /> : <Cake className="h-6 w-6" />}
          </button>
          <div className="min-w-0 flex-1">
            <button
              type="button"
              onClick={() => navigate('/birthdays')}
              className="text-left text-sm sm:text-base font-semibold text-pink-900 hover:underline"
              data-testid="birthday-banner-heading"
            >
              {heading}
            </button>
            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
              {visible.map((b) => (
                <button
                  key={`${b.type}-${b.id || b.npn}`}
                  type="button"
                  onClick={() => navigate(b.type === 'agent' ? `/agents/${b.npn}` : `/clients/${b.id}`)}
                  data-testid={`banner-birthday-${b.type}-${b.id || b.npn}`}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border transition text-xs sm:text-sm text-slate-700 ${b.t65 ? 'bg-amber-50 border-amber-300 hover:border-amber-500 hover:bg-amber-100' : 'bg-white border-pink-200 hover:border-pink-400 hover:bg-pink-50'}`}
                  title={b.t65 ? 'Turning 65 — Medicare T-65' : `Turning ${b.turning_age}`}
                >
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${b.t65 ? 'bg-amber-200 text-amber-800' : 'bg-pink-200 text-pink-800'}`}>
                    {(b.first_name?.[0] || '?').toUpperCase()}
                  </span>
                  <span className="truncate max-w-[120px]">{b.first_name} {b.last_name}</span>
                  {b.t65 && (
                    <span className="px-1.5 py-0.5 rounded-full bg-amber-500 text-white text-[9px] font-bold tracking-wide" data-testid="banner-t65-badge">T-65</span>
                  )}
                  {b.is_today ? (
                    <span className="text-pink-600 font-medium">today</span>
                  ) : (
                    <span className="text-slate-400">· {b.days_until === 1 ? 'tmrw' : `${b.days_until}d`}</span>
                  )}
                </button>
              ))}
              {remainder > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs text-pink-700 hover:bg-pink-100"
                  onClick={() => navigate('/birthdays')}
                  data-testid="birthday-banner-view-all"
                >
                  +{remainder} more <ChevronRight className="h-3 w-3 ml-0.5" />
                </Button>
              )}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="shrink-0 text-pink-700/60 hover:text-pink-900 self-start sm:self-center"
          aria-label="Dismiss"
          data-testid="birthday-banner-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </CardContent>
    </Card>
  );
}
